import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import * as sessionReplay from '@amplitude/session-replay-browser';
import { UserIdRegex } from '@squaredup/tenants';
import { hasProperty } from '@squaredup/utilities';
import { debounce } from 'lodash';
import Auth from 'services/Auth';
import { systemThemeDataAttribute, ThemeOptions } from 'ui/hooks/useTheme';
import { v4 as uuidv4 } from 'uuid';
import { getIsSyntheticTenant, setIsSyntheticTenant } from './analyticsReset';
import { environment, isProd, isTestEnvironment } from './environment';

// Tenant name patterns we never want to send to Customer.io
const SYNTHETIC_TENANT_NAME_PATTERNS = [/^Disabled or deleted tenant$/u, /^E2E-ADO-/u, /^API-LCL-/u];

// User ID pattern we want to gate on for all client-side analytics
const saasUserIdPattern = new RegExp(UserIdRegex, 'iu');
const isValidSaasUserId = (id: string | undefined): id is string =>
    typeof id === 'string' && saasUserIdPattern.test(id);

// Simple utility function to ensure that any issues with sending those events doesn't take down our code!
export default function trackEvent(eventName: string, payload: any) {
    try {
        const context: any = Auth?.user?.tenant ? { groupId: Auth.user.tenant } : {};
        if (hasProperty(window, 'analytics') && typeof window.analytics === 'object' && window.analytics != null) {
            window.analytics.track(eventName, payload, context); //eslint-disable-line no-undef
        }

        if (!getIsSyntheticTenant() && Auth?.user?.name && window.customerIO) {
            window.customerIO.track(eventName, payload, context);
        }

        const isChatWidgetEvent = typeof eventName === 'string' && eventName.toLowerCase().startsWith('chat widget ');

        if (
            hasProperty(window, 'LiveChatWidget') &&
            typeof window.LiveChatWidget === 'object' &&
            window.LiveChatWidget != null &&
            !isChatWidgetEvent
        ) {
            const normalizedEventName = eventName.toLowerCase().replace(/[^a-z0-9]/gu, '');
            window.LiveChatWidget.call('update_session_variables', { [normalizedEventName]: 'true' });
            if (eventName.toLowerCase().startsWith('plugin ') && payload?.type) {
                window.LiveChatWidget.call('update_session_variables', { plugin: payload.type });
            }
        }
    } catch (e) {
        // Never expect to hit this, it would indicate we're mis configured, or someone has messed
        // with index.html, around how we init segment
        console.error('Analytics error', e); //eslint-disable-line no-console
    }
}

export const trackEventDebounced = debounce((eventName, payload) => {
    trackEvent(eventName, payload);
}, 1000);

/**
 * Records the theme a user chose alongside the theme they actually see as Amplitude user properties.
 * A 'system' preference resolves from the OS colour scheme in the browser and is never stored, so the
 * effective theme is the only way to tell how many users following the OS see light rather than dark.
 */
export const setThemeUserProperties = (themePreference: ThemeOptions, effectiveTheme: ThemeOptions): void => {
    try {
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set('Theme Preference', themePreference);
        identifyEvent.set('Effective Theme', effectiveTheme);
        amplitude.identify(identifyEvent);
    } catch (error) {
        console.error('Analytics error', error); //eslint-disable-line no-console
    }
};

export const identifyUser = (tenantName: string, tenantStage: string) => {
    setIsSyntheticTenant(SYNTHETIC_TENANT_NAME_PATTERNS.some((pattern) => pattern.test(tenantName)));
    const userName = Auth.user?.name;
    const userId = isValidSaasUserId(Auth.user?.id) ? Auth.user.id : undefined;

    if (hasProperty(window, 'analytics') && typeof window.analytics === 'object' && window.analytics != null) {
        if (userId) {
            window.analytics.identify(userId, {
                email: userName,
                name: userName,
                username: userName
            });
        }

        if (Auth.user?.tenant) {
            window.analytics.group(Auth.user.tenant, {
                name: tenantName,
                activeStage: tenantStage
            });
        }
    }

    if (!getIsSyntheticTenant() && userName && window.customerIO) {
        if (userId) {
            window.customerIO.identify(userId, {
                email: userName
            });
        }

        if (Auth.user?.tenant) {
            window.customerIO.group(Auth.user.tenant, {
                objectTypeId: 3,
                name: tenantName,
                activeStage: tenantStage
            });
        }
    }
};

const urlParams = new URLSearchParams(window.location.search);

const ignoreDomains = ['squaredup.com', 'squaredup.cloud'];

export const initAmplitude = async (
    tenantId: string,
    tenantName: string,
    tenantStage: string,
    tierTemplate: string
) => {
    const enableClient = (isProd && !isTestEnvironment) || urlParams.get('analytics') === 'enabled';
    const isUserIgnoredDomain = ignoreDomains.some((domain) => Auth.user?.name.endsWith(`@${domain}`));
    const enableReplay = (tierTemplate === 'free' && !isUserIgnoredDomain) || urlParams.get('analytics') === 'enabled';
    const apiKey = isProd ? '352b152dee41e0c885c8ed5d6bf18f56' : 'd7744e19363da5203cc22e4c8bc7211c';

    function getSessionIdCookie(): number {
        const match = document.cookie.match('(^|;)\\s*analytics_session_id=(\\d+)(?=;|$)')?.pop();
        return match ? parseInt(match) : 0;
    }

    function setSessionIdCookie(sessionId: number) {
        document.cookie = `analytics_session_id=${sessionId};`;
    }

    if (enableClient) {
        amplitude.add(engagementPlugin());
        amplitude.init(apiKey, Auth.user?.id, {
            serverZone: 'EU',
            flushQueueSize: 1,
            cookieOptions: { domain: window.location.hostname },
            autocapture: {
                attribution: true,
                elementInteractions: true,
                fileDownloads: true,
                formInteractions: true,
                pageViews: true,
                sessions: true
            }
        });

        if (enableReplay) {
            await sessionReplay.init(apiKey, {
                serverZone: 'EU',
                deviceId: window.analytics?.user?.().anonymousId?.() ?? uuidv4(),
                sessionId: getSessionIdCookie()
            }).promise;

            window.analytics.addSourceMiddleware(async ({ payload, next }) => {
                const storedSessionId = getSessionIdCookie();
                const nextSessionId =
                    (payload.obj.integrations?.['Actions Amplitude'] as { session_id?: number })?.session_id || 0;
                if (storedSessionId < nextSessionId) {
                    setSessionIdCookie(nextSessionId);
                    await sessionReplay.setSessionId(nextSessionId).promise;
                }
                next(payload);
            });

            window.analytics.addSourceMiddleware(({ payload, next }) => {
                const sessionReplayProperties = sessionReplay.getSessionReplayProperties();
                if (payload.type() === 'track') {
                    payload.obj.properties = {
                        ...payload.obj.properties,
                        ...sessionReplayProperties
                    };
                }
                next(payload);
            });
        }

        // add the user to a group per trait to allow for filtering
        // i.e. all users in ten-abc, all users on stage-123, all users on free tier, etc.
        amplitude.setGroup('id', tenantId);
        amplitude.setGroup('stage', tenantStage);
        amplitude.setGroup('tier', tierTemplate);
        amplitude.setGroup('environment', environment);

        // enrich the tenant group with its traits
        const groupEvent = new amplitude.Identify();
        groupEvent.set('name', tenantName);
        groupEvent.set('stage', tenantStage);
        groupEvent.set('tier', tierTemplate);
        groupEvent.set('environment', environment);
        amplitude.groupIdentify('id', tenantId, groupEvent);

        //set the theme for any engagement popups
        const engagement = (window as any).engagement;
        engagement?.setThemeMode(
            document.documentElement.getAttribute(systemThemeDataAttribute) === ThemeOptions.light
                ? 'lightMode'
                : 'darkMode'
        );

        if (Auth.user?.name) {
            const identifyEvent = new amplitude.Identify();
            identifyEvent.set('email', Auth.user.name);
            amplitude.identify(identifyEvent);
        }
    }
};
